import * as UserActions from './user.actions';

import {
  DisplayOptions,
  EMPTY_REPLY_TO_ADDRESS,
  LoginErrors,
  LoginStatusFormField,
  PreferenceCode,
  PreferenceElement,
  PreferenceGroup,
  Region,
  Template,
  TemplateCode,
  UpdatePreference,
  UserExperience,
  UserState
} from '@store/user/user.model';
import * as SearchBoardActions from '@store/search-board/search-board.actions';
import { PreferenceUtils } from '@shared/utils/preference-utils';

export const userInitialState: UserState = {
  user: {
    prefAndTempData: {
      searchableFields: [],
      gridHeaders: [],
      searchResultTablePreference: null,
      customCardsAttributes: [],
      searchResultCardsPreference: null,
      templateList: [],
      preferenceGroupList: []
    },
    hasAccessMap: {},
    productPromotions: [],
    pdrReportInaccuracyEnabled: false,
    shareReportLinkEnabled: false,
    replyToEmailAddress: null,
  },
  isDirectLinkLoading: false,
  isLoginInProgress: false,
  loginErrors: null,
  isMobile: null,
  helpPageContactInfo: null,
  premiumSearchMinimumZoom: null
};

export function UserReducer(state: UserState = userInitialState,
                            action: UserActions.All | SearchBoardActions.SaveCustomMySearchFromModalSuccess): UserState {
  let payload;
  switch (action.type) {
    case UserActions.LOGIN:
    case UserActions.REFRESH_PAGE:
    case UserActions.DIRECT_LINK_LOGIN:
      return {
        ...state,
        isLoginInProgress: true
      };

    case UserActions.LOGIN_SUCCESS:
      payload = action.payload;
      const appVersion = payload.appVersion;
      const navigationConfig = payload.navigationConfig;
      const userInfo = payload.userAccessOutput ? payload.userAccessOutput.userInfo : null;
      const statusCode = payload.statusCode;
      const googleMapKey = payload.googleMapKey;
      const mixpanelProjectToken = payload.mixpanelProjectToken;
      const mixpanelResId = payload.mixpanelResId;
      const showSurveyLink = payload.showSurveyLink;
      const propertyIntelligenceDashboardEnabled: boolean = payload.propertyIntelligenceDashboardEnabled;
      return {
        ...state,
        loginErrors: null,
        premiumSearchMinimumZoom: payload.premiumSearchMinimumZoom,
        user: {
          ...state.user,
          ...userInfo,
          statusCode,
          googleMapKey,
          mixpanelProjectToken,
          mixpanelResId,
          showSurveyLink,
          appVersion,
          prefAndTempData: {
            ...payload.prefAndTempData,
            gridHeaders: payload.gridHeaders
          },
          provisionedGeos: payload.provisionedGeos,
          hasAccessMap: payload.hasAccessMap,
          dashboardWidgets: payload.dashboardWidgets,
          dashBoardEnabled: payload.dashBoardEnabled,
          propertyIntelligenceDashboardEnabled,
          isPinMember: payload.isPinMember,
          rssNewsFeeds: payload.rssNewsFeeds,
          userSelectedRssNewsFeed: payload.userSelectedRssNewsFeed,
          propertyIntelligencePreference: payload.propertyIntelligencePreference,
          cbsaMap: payload.cbsaMap,
          sendPostCardsEnabled: payload.sendPostCardsEnabled,
          newsFeedWidgetEnabled: payload.newsFeedWidgetEnabled,
          standAlonePropIntelEnabled: payload.standAlonePropIntelEnabled ?? false,
          propertyIntelligenceWalkthroughEnabled: payload.propertyIntelligenceWalkthroughEnabled,
          noSurvey: payload.noSurveyMls.split(',').includes(userInfo.groupCode.toUpperCase()),
          navigationConfig,
          userExperience: getRefreshedUserExperience(state?.user?.userExperience, payload.userExperience),
        }
      };

    case UserActions.GET_LOGIN_DATA_SUCCESS:
      payload = action.payload;

      return {
        ...state,
        loginErrors: null,
        premiumSearchMinimumZoom: payload.premiumSearchMinimumZoom ?? state?.premiumSearchMinimumZoom,
        isLoginInProgress: false,
        user: {
          ...state.user,
          shapeMaxArea: payload.shapeMaxArea ?? state?.user?.shapeMaxArea,
          sessionTimeoutDefault: payload.sessionTimeoutDefault ?? state?.user?.sessionTimeoutDefault,
          sessionTimeoutMsgShowDelay: payload.sessionTimeoutMsgShowDelay ?? state?.user?.sessionTimeoutMsgShowDelay,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            ...payload.prefAndTempData,
            gridHeaders: payload.gridHeaders,
            mySearchTemplates: payload.prefAndTempData.mySearchTemplates ?? state?.user?.prefAndTempData?.mySearchTemplates,
            templateList: [
              ...state.user.prefAndTempData.templateList,
              ...payload.prefAndTempData.templateList
            ],
            preferenceGroupList: [
              ...state.user.prefAndTempData.preferenceGroupList,
              ...payload.prefAndTempData.preferenceGroupList
            ],
            searchableFields: payload.searchableFields ?? state?.user?.prefAndTempData?.searchableFields,
          },
          maxEmailDownloadCards: payload.maximumEmailDownloadCards ?? state?.user?.maxEmailDownloadCards,
          maxEmailDownloadMapTable: payload.maximumEmailDownloadMapTable ?? state?.user?.maxEmailDownloadMapTable,
          maxEmailDownloadReportsPDR: payload.maximumEmailDownloadReportsPDR ?? state?.user?.maxEmailDownloadReportsPDR,
          maxNumberOfNeighbors: payload.maximumNumberOfNeighbrs ?? state?.user?.maxNumberOfNeighbors,
          maxNumberOfComparables: payload.maximumNumberOfComparables ?? state?.user?.maxNumberOfComparables,
          maxNumberOfMlsPhotos: payload.maximumNumberOfMlsPhotos ?? state?.user?.maxNumberOfMlsPhotos,
          hasAccessMap: payload.hasAccessMap ?? state?.user?.hasAccessMap,
          valueMap: payload.valueMap ?? state?.user?.valueMap,
          productPromotions: payload.productPromotions ?? state?.user?.productPromotions,
          pdrReportInaccuracyEnabled: payload.pdrReportInaccuracyEnabled,
          shareReportLinkEnabled: payload.shareReportLinkEnabled ?? false,
          pdrCommunityInsightsConfiguration: payload.pdrCommunityInsightsConfiguration,
          premiumProductPrices: payload.premiumProductPrices ?? state?.user?.premiumProductPrices,
          userExperience: state?.user?.userExperience,
        }
      };

    case UserActions.UPDATE_DIRECT_TO_AGENT_CREDITS: {
      const current = state?.user?.userExperience;
      if (!current?.['DIRECT_TO_AGENT']) {
        return state;
      }
      return {
        ...state,
        user: {
          ...state.user,
          userExperience: {
            ...current,
            DIRECT_TO_AGENT: {
              ...current['DIRECT_TO_AGENT'],
              availableCredits: action.remainingCredits,
            }
          }
        }
      };
    }

    case UserActions.GET_MY_SEARCH_DATA_SUCCESS:
      payload = action.payload;

      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            componentPreferenceMapping: {
              ...state.user.prefAndTempData.componentPreferenceMapping,
              ...payload.prefAndTempData.componentPreferenceMapping
            },
            componentTemplateMapping: {
              ...state.user.prefAndTempData.componentTemplateMapping,
              ...payload.prefAndTempData.componentTemplateMapping
            },
            mySearchTemplates: payload.prefAndTempData.mySearchTemplates,
            templateList: [
              ...state.user.prefAndTempData.templateList,
              ...payload.prefAndTempData.templateList
            ],
            preferenceGroupList: [
              ...state.user.prefAndTempData.preferenceGroupList,
              ...payload.prefAndTempData.preferenceGroupList
            ],
            searchableFields: payload.searchableFields,
          }
        }
      };

    case UserActions.UPDATE_EULA_STATUS:
      return {
        ...state,
        user: {
          ...state.user,
          passportUserInfo: {
            ...state.user.passportUserInfo,
            eulaStatus: DisplayOptions.YES
          }
        }
      };

    case UserActions.LOGOUT_SUCCESS:
      return {
        ...state,
        user: null
      };

    case UserActions.SET_MOBILE:
      return {
        ...state,
        isMobile: action.payload
      };

    case UserActions.LOGIN_FAILURE:
      payload = action.payload;
      const errorField = LoginStatusFormField[payload.statusCode] || 'loginError';
      const loginErrors = {[errorField]: payload.statusMessage} as LoginErrors;
      return {
        ...state,
        user: {
          mixpanelProjectToken: action.payload.mixpanelProjectToken,
          mixpanelResId: action.payload.mixpanelResId
        },
        loginErrors
      };

    case UserActions.SAVE_EMAIL_PREFERENCE_ACTION:
      const newPrefGroupList = state.user.prefAndTempData.preferenceGroupList
        .map((prefGroup: PreferenceGroup) => {
          if (prefGroup.prefGroupCode !== PreferenceCode.PG_RL_GENERAL_PREFERENCES) {
            return prefGroup;
          }
          const newPrefElements = prefGroup.preferenceElements.map((prefElement: PreferenceElement) =>
            prefElement.prefCode !== PreferenceCode.PG_GENPREF_USER_EMAIL ? prefElement :
              PreferenceUtils.setPreferenceValue(prefElement, action.payload)
          );
          return {...prefGroup, preferenceElements: newPrefElements};
        });
      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            preferenceGroupList: newPrefGroupList
          }
        }
      };

    case UserActions.GET_REPLY_TO_EMAIL_ADDRESS_SUCCESS:
      return {
        ...state,
        user: {
          ...state.user,
          replyToEmailAddress: action.payload ? action.payload : EMPTY_REPLY_TO_ADDRESS,
        }
      }

    case UserActions.SAVE_EMAIL_SIGNATURE_SUCCESS:
      const signature = action.payload;
      const prefGroupList = state.user.prefAndTempData.preferenceGroupList
        .map((prefGroup: PreferenceGroup) => {
          if (prefGroup.prefGroupCode !== PreferenceCode.PG_RL_GENERAL_PREFERENCES) {
            return prefGroup;
          }
          const newPrefElements = prefGroup.preferenceElements.map((prefElement: PreferenceElement) =>
            prefElement.prefCode !== PreferenceCode.PG_GENPREF_EMAIL_SIGNATURE ? prefElement :
              ({
                ...prefElement,
                preferenceValue: {
                  ...prefElement.preferenceValue,
                  values: [signature]
                }
              })
          );
          return {...prefGroup, preferenceElements: newPrefElements};
        });
      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            preferenceGroupList: prefGroupList
          }
        }
      };

    case UserActions.GET_CUSTOM_GRID_HEADERS_SUCCESS:
      payload = action.payload;
      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            searchResultTablePreference: payload
          }
        }
      };

    case UserActions.GET_CUSTOM_CARDS_ATTRIBUTES_SUCCESS:
      payload = action.payload;
      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            searchResultCardsPreference: payload
          }
        }
      };

    case UserActions.SAVE_NEW_CUSTOM_GRID_HEADERS_SUCCESS:
      const newGridHeaders = action.payload;
      const newTemplateList = state.user.prefAndTempData.templateList
        .map((template: Template) => template.templateCode === TemplateCode.TMPL_PROPERTY_GRID_DISPLAY_ELEMENTS
          ? ({...template, dataElementList: newGridHeaders})
          : template);
      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            templateList: newTemplateList,
            gridHeaders: newGridHeaders
          }
        }
      };

    case UserActions.SAVE_CUSTOM_CARDS_ATTRIBUTES_SUCCESS:
      const newTemplateListWithCardsAtt = state.user.prefAndTempData.templateList.map((template: Template) =>
        template.templateCode === TemplateCode.TMPL_PROPERTY_DISPLAY_CARD_ELEMENTS ? ({
          ...template,
          childTemplateList: template.childTemplateList.map((tmpl: Template) =>
            tmpl.templateCode === TemplateCode.TMPL_PROPERTY_DISPLAY_CARD_ELEMENTS_CONFIG ? ({
              ...tmpl,
              dataElementList: action.payload
            }) : tmpl)
        }) : template);

      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            templateList: newTemplateListWithCardsAtt
          }
        }
      };

    case UserActions.SAVE_REGIONS_SUCCESS:
      return {
        ...state,
        user: {
          ...state.user,
          provisionedGeos: getNewProvisionedGeos(state.user.provisionedGeos,
            action.payload.regions),
        }
      };

    case SearchBoardActions.SAVE_SELECTED_EDIT_SEARCH_FROM_MODAL_SUCCESS:
    case UserActions.SAVE_CUSTOM_MY_SEARCH_SUCCESS:
      payload = (action as UserActions.SaveCustomMySearchSuccess | SearchBoardActions.SaveCustomMySearchFromModalSuccess).payload;

      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            mySearchTemplates: (state.user.prefAndTempData.mySearchTemplates || []).concat({
              ...payload,
              modifiedDate: new Date().toJSON()
            })
          }
        },
      };

    case UserActions.GET_GEO_DATA_SUCCESS:
      payload = action.payload;
      return {
        ...state,
        user: {
          ...state.user,
          geoData: {
            cbsaMap: action.payload.cbsaMap,
            zipCodes: (payload.zipCodes as Array<string>).map((zipCode: string) => ({ id: zipCode, label: zipCode })),
            cbsaList: (payload.cbsaList as Array<string>).map((cbsa: string) => ({ id: '', label: cbsa })),
            countyList: state.user.provisionedGeos.map((county: Region) => ({ id: county.fips, label: `${county.countyName}, ${county.stateCode}` })),
          },
        }
      }

    case UserActions.REMOVE_CUSTOM_MY_SEARCH_SUCCESS:
      const {removedTemplateCode} = action.payload;
      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            mySearchTemplates: state.user.prefAndTempData.mySearchTemplates
              .filter((temp: Template) => temp.templateCode !== removedTemplateCode)
          }
        }
      };

    case UserActions.UPDATE_CUSTOM_MY_SEARCH_SUCCESS:
      const updatedTemplate: Template = action.payload;
      const oldTemplates = state.user.prefAndTempData.mySearchTemplates
        .filter((temp: Template) => temp.templateCode !== updatedTemplate.templateCode);

      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            mySearchTemplates: [...oldTemplates, {...updatedTemplate, modifiedDate: new Date().toJSON()}]
          }
        }
      };

    case UserActions.SAVE_PREFERENCE_GROUP_SUCCESS:
    case UserActions.SAVE_REPORTS_PREFERENCE_GROUP_SUCCESS:
      const updatedPreference: Array<UpdatePreference> = (action as UserActions.SavePreferenceGroupSuccess).payload;
      const oldPrefGroupList = state.user.prefAndTempData.preferenceGroupList;
      return {
        ...state,
        user: {
          ...state.user,
          prefAndTempData: {
            ...state.user.prefAndTempData,
            preferenceGroupList: updatePreferenceGroupList(oldPrefGroupList, updatedPreference)
          }
        }
      };

    case UserActions.GET_DIRECT_LINK_DATA:
      return {
        ...state,
        isDirectLinkLoading: true
      };

    case UserActions.GET_DIRECT_LINK_DATA_SUCCESS:
      return {
        ...state,
        isDirectLinkLoading: false
      };

    case UserActions.GET_HELP_PAGE_CONTACT_INFO_SUCCESS:
      return {
        ...state,
        helpPageContactInfo: action.payload
      };

    case UserActions.UPDATE_DASHBOARD_WIDGETS:
      return {
        ...state,
        user: {
          ...state.user,
          dashboardWidgets: action.payload
        }
      }
    case UserActions.UPDATE_PROP_INTEL_PREFERENCE_SUCCESS:
      return {
        ...state,
        user: {
          ...state.user,
          propertyIntelligencePreference: {
            ...action.payload,
          }
        }
      }
    case UserActions.SAVE_RSS_FEED_SUCCESS:
      return {
        ...state,
        user: {
          ...state.user,
          userSelectedRssNewsFeed: action.payload
        }
      }
    case UserActions.DISABLE_PROPERTY_INTELLIGENCE_WALKTHROUGH_PREFERENCE:
      return {
        ...state,
        user: {
          ...state.user,
          propertyIntelligenceWalkthroughEnabled: false
        }
      }
    default: {
      return state;
    }
  }
}

function getNewProvisionedGeos(allRegions: Array<Region>, selectedRegions: Array<Region>): Array<Region> {
  return allRegions.map((region: Region) =>
    ({
      ...region,
      selected: selectedRegions.some((selectedRegion: Region) => selectedRegion.fips === region.fips)
    })
  );
}

/**
 * Merges a refreshed D2A user-experience into the current state.
 * Returns `refreshed` when present; otherwise strips the `DIRECT_TO_AGENT` slice
 * from `current` to clear stale D2A state when the callback response omits userExperience,
 * returning the remaining properties or `undefined` if nothing remains.
 */
function getRefreshedUserExperience(current?: UserExperience, refreshed?: UserExperience): UserExperience | undefined {
  if (refreshed) {
    return refreshed;
  }

  if (!current?.DIRECT_TO_AGENT) {
    return current;
  }

  const { DIRECT_TO_AGENT, ...remainingPackages } = current;
  return Object.keys(remainingPackages).length > 0 ? remainingPackages : undefined;
}

function updatePreferenceGroupList(oldPrefGroupList: Array<PreferenceGroup>,
                                   updatedPreferences: Array<UpdatePreference>): Array<PreferenceGroup> {
  const newPrefGroupList = oldPrefGroupList.map((pGroup: PreferenceGroup) => {
    return {
      ...pGroup,
      preferenceElements: [...pGroup.preferenceElements]
    };
  });

  updatedPreferences.forEach((updatedPref: UpdatePreference) => {
    const updatedPrefGroupIndex = newPrefGroupList.findIndex((prefGroup: PreferenceGroup) =>
      prefGroup.prefGroupCode === updatedPref.prefGroupCode);

    if (updatedPrefGroupIndex === -1) {
      return;
    }

    updatedPref.preferenceElementsList.forEach((preferenceElement: PreferenceElement) => {
      const index = newPrefGroupList[updatedPrefGroupIndex].preferenceElements.findIndex((prefElement: PreferenceElement) =>
        prefElement.prefCode === preferenceElement.prefCode);

      index === -1 ?
        newPrefGroupList[updatedPrefGroupIndex].preferenceElements.push(preferenceElement) :
        newPrefGroupList[updatedPrefGroupIndex].preferenceElements[index] = preferenceElement;
    });
  });

  return newPrefGroupList;
}
